import classNames from "classnames"
import React, { useCallback } from "react"

import { Analytics } from "@bounce/analytics"
import type { DateTime } from "@bounce/date"
import { useTranslation } from "@bounce/i18n"
import type { ButtonProps } from "@bounce/web-components"

import { DatePicker } from "./DatePicker"
import { useSearchBar } from "./useSearchBar"
import { AddressField } from "@/components/Generic/AddressField/AddressField"
import { useStickyCTA } from "@/contexts/StickyCTAContext"
import { InteractionEvents } from "@/libs/analytics/events"
import type { PageType } from "@/types"

import SearchIcon from "@bounce/assets/icons/Search.svg"

export type SearchBarProps = {
  address?: string
  button: React.ReactElement<ButtonProps<"a"> & { "data-chromatic": string; "data-testid": string }>
  className?: string
  from: PageType
  initialQuery?: string
  isDatepickerVisible?: boolean
}

export const SearchBar = ({
  address,
  button,
  className,
  from,
  initialQuery = "",
  isDatepickerVisible = false,
}: SearchBarProps) => {
  const { t } = useTranslation()
  const { ref: stickyCTARef } = useStickyCTA()
  const { checkInDate, handleUpdateCheckInDate, onSelectRecommendation, onSearch, setQuery, searchUrl } = useSearchBar({
    from,
    initialQuery,
  })

  const onToggleDatepicker = useCallback(
    (value: boolean) => Analytics.track(InteractionEvents.DatepickerToggled, { open: value, from }),
    [from],
  )
  const onUpdateCheckIn = useCallback(
    (date: DateTime) => {
      Analytics.track(InteractionEvents.DatepickerCheckInUpdated, { from })
      handleUpdateCheckInDate(date)
    },
    [from, handleUpdateCheckInDate],
  )

  return (
    <form onSubmit={onSearch} className={classNames("flex w-full flex-col", className)}>
      <div className="relative flex w-full rounded-full border border-neutral-300 bg-white text-neutral-800 lg:max-w-[500px]">
        <AddressField
          className="pl-2 md:pl-4"
          defaultValue={address ?? initialQuery}
          leftIcon={<SearchIcon />}
          onChange={setQuery}
          onSelectRecommendation={onSelectRecommendation}
          placeholder={t("cmp.searchBar.placeholder", "Search place or address")}
        />
        {isDatepickerVisible && (
          <div className="items-center md:flex">
            <DatePicker
              checkInDate={checkInDate}
              className="md:mr-3"
              leftIcon
              onToggle={onToggleDatepicker}
              onUpdate={onUpdateCheckIn}
            />
          </div>
        )}
      </div>
      <div ref={stickyCTARef}>
        {React.cloneElement(button, {
          "data-chromatic": "ignore",
          "data-testid": "search-bar:cta",
          className: classNames("mx-auto mt-4 lg:mx-0", button.props.className),
          color: button.props.color ?? "primary",
          href: button.props.href ?? searchUrl,
          onClick: onSearch,
          rel: "nofollow",
          textClassName: classNames(button.props.textClassName, "line-clamp-1"),
        })}
      </div>
    </form>
  )
}
